import React, { useState, useReducer } from "react";
import { useLogin, useNotify, useLocale, Notification, useTranslate } from "react-admin";
import { Typography, Grid } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import CachedIcon from "@material-ui/icons/Cached";
import clsx from "clsx";
import { lfLoginRequest } from "../authProvider";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import getLoginType from "utils/getLoginType";

const initialState = {
  loading: false,
  loginRes: { loginPhase: { status: "USER_PASSWORD" } },
};

const reducer = (state, { type, payload }) => {
  switch (type) {
    case "startSubmit":
      return {
        ...state,
        loading: true,
      };

    case "handleSubmit":
      return {
        ...state,
        loginRes: payload.loginRes,
      };

    case "finishSubmit":
      return {
        ...state,
        loading: false,
      };

    default:
      throw new Error();
  }
};

const styles = {
  form: {
    width: "100%",
  },
  entradas: {
    font: "inherit",
    border: "0.5px solid #444",
    height: "53px",
    width: "230px",
    padding: "0px 15px",
    fontSize: "16px",
    margin: ".5rem",
  },
  etiqueta: {
    fontSize: "1.04rem",
    lineHeight: "1.25rem",
    marginRight: ".5rem",
    color: "black !important",
  },
  etiquetaPinDest: {
    marginTop: "10px",
  },
  formRows: {
    marginBottom: "10px",
  },
  posRelative: {
    position: "relative",
    clear: "both",
    width: "100%",
    height: "70px",
  },
  btnSubmit: {
    minWidth: "101.375px",
    margin: ".5rem",
    textTransform: "lowercase",
    fontWeight: "bold",
  },
  submitMessage: {
    marginLeft: ".5rem",
    minWidth: "100px",
  },
  spin: {
    color: "rgba(0, 0, 0, 0.26)",
    animation: "$spin 2500ms infinite linear",
  },
  buttonArea: {
    marginTop: "2rem",
  },

  "@keyframes spin": {
    from: {
      transform: "rotate(360deg)",
    },
    to: {
      transform: "rotate(0deg)",
    },
  },
  pinOption: {
    display: "flex",
    margin: "10px",
  },
  otpPinDests: {
    borderLeft: "1px black dotted",
  },
  pinSelectInput: {
    marginRight: "8px",
  },
  confirmPinDest: {
    marginTop: "20px",
  },
  alert: {
    marginBottom: "30px",
  },
};

const InfoMessage = ({ className, classes, textKey, options = {} }) => {
  const translate = useTranslate();

  return (
    <Alert severity="info" classes={classes} className={className}>
      <Typography variant="body2" dangerouslySetInnerHTML={{ __html: translate(textKey, options) }} />
    </Alert>
  );
};

const ButtonIcon = ({ className, loading }) => {
  if (!loading) {
    return null;
  }

  return <CachedIcon className={className} />;
};

const Form = (props) => {
  const { classes } = props;
  const notify = useNotify();
  const translate = useTranslate();
  const login = useLogin();
  const locale = useLocale();
  const loginType = getLoginType();

  const { executeRecaptcha } = useGoogleReCaptcha();

  const [{ loading, loginRes }, dispatch] = useReducer(reducer, initialState);

  // campos de los formularios
  const [username, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [impersonationCustomerId, setImpersonationCustomerId] = useState("");
  const [otpPinDest, setOtpPinDest] = useState(null);
  const [otpPin, setOtpPin] = useState("");

  const handleLoginRes = (loginRes) => {
    // check de loginRes.sessionToken para hacerlo compatible con api anterior de atlas
    if ( ! loginRes.loginPhase || loginRes.sessionToken || loginRes.loginPhase.status === "LOGGED_IN" || loginRes.loginPhase.status === "ERROR" ) {
      return login(loginRes);
    }

    dispatch({
      type: "handleSubmit",
      payload: { loginRes: loginRes },
    });

    return Promise.resolve(loginRes);
  };

  const submit = (e) => {
    e.preventDefault();
    dispatch({ type: "startSubmit" });

    executeRecaptcha("tokenAction")
      .then((token) => {
        const base_params = {
          username,
          password,
          captcha: token,
          idioma: locale,
          huellaDispositivo: "gj-test-01",
          loginPhaseStatus: loginRes.loginPhase.status,
          ...(loginType === "agente" && { impersonationCustomerId: impersonationCustomerId }),
        };
        const extra_params =
          loginRes.loginPhase.status === "OTP_PIN_DESTS"
            ? { credencialSeguridad: otpPinDest.dest, tipoEnvio: otpPinDest.type }
            : loginRes.loginPhase.status === "OTP_PIN"
            ? {
                otpPin: otpPin,
                otpPinOperationId: loginRes.loginPhase.otpPinOperationId,
                fechaExpiracion: loginRes.loginPhase.fechaExpiracion,
                credencialSeguridad: loginRes.loginPhase.destinatario,
              }
            : {};

        lfLoginRequest({ ...base_params, ...extra_params }, loginType)
          .then(handleLoginRes)
          .catch((e) => notify(e, "error"))
          .finally(() => dispatch({ type: "finishSubmit" }));
      })
      .catch((e) => notify(e, "error"));
  };

  return (
    <>
      <form onSubmit={submit} className={loginRes.loginPhase.status !== "USER_PASSWORD" && classes.form}>
        {/* formulario normal */}
        {loginRes.loginPhase.status === "USER_PASSWORD" && (
          <>
            <Grid container direction="column" justify="center" alignItems="center">
              <Grid
                container
                direction="row"
                justify="center"
                alignItems="baseline"
              >
                <Grid item xs={6} container direction="row" justify="flex-end">
                  <label htmlFor="username" className={clsx(classes.etiqueta, "etiquetaEmailNifTelefono")}>
                    {translate("ra.auth.email_nif_tlf")}
                  </label>
                  <label htmlFor="username" className={clsx(classes.etiqueta, "etiquetaUsuario")}>
                    {translate("ra.auth.usuario")}
                  </label>
                </Grid>
                <Grid item xs={6}>
                  <input
                    className={clsx(classes.entradas, "entradas")}
                    name="username"
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUserName(e.target.value)}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid container direction="column" justify="center" alignItems="center">
              <Grid
                container
                direction="row"
                justify="center"
                alignItems="baseline"
              >
                <Grid item xs={6} container direction="row" justify="flex-end">
                  <label htmlFor="password" className={clsx(classes.etiqueta, "etiquetaPassword")}>
                    {translate("ra.auth.password")}
                  </label>
                </Grid>
                <Grid item xs={6}>
                  <input
                    className={clsx(classes.entradas, "entradas")}
                    id="password"
                    name="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </Grid>
              </Grid>
            </Grid>
            {loginType === "agente" && 
            <>
              <Grid container direction="column" justify="center" alignItems="center">
                <Grid
                  container
                  direction="row"
                  justify="center"
                  alignItems="baseline"
                >
                  <Grid item xs={6} container direction="row" justify="flex-end">
                    <label htmlFor="impersonationCustomerId" className={clsx(classes.etiqueta, "etiquetaIdCliente")}>
                      {translate("ra.auth.idCliente")}
                    </label>
                  </Grid>
                  <Grid item xs={6}>
                    <input
                      className={clsx(classes.entradas, "entradas")}
                      id="impersonationCustomerId"
                      name="impersonationCustomerId"
                      type="text"
                      value={impersonationCustomerId}
                      onChange={(e) => setImpersonationCustomerId(e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </>}
          </>
        )}

        {/* formulario destino pin */}
        {loginRes.loginPhase.status === "OTP_PIN_DESTS" && (
          <>
            <Grid container direction="column">
              <Grid item xs={9}>
                <InfoMessage textKey="login.pinDest" className={classes.alert} />
              </Grid>
            </Grid>
            <Grid item xs={9}>
              <Grid container direction="column" justify="center" alignItems="center">
                <Grid container direction="row">
                  <Grid item xs className={clsx(classes.etiquetaPinDest)}>
                    <Grid container direction="row">
                      <span className={classes.etiqueta}>{translate("ra.auth.otp_pin_dest")}</span>
                    </Grid>
                  </Grid>
                  <Grid item xs={9} className={classes.otpPinDests}>
                    {loginRes.loginPhase.otpPinDests.map((otpPinDest) => (
                      <div key={`Key-${otpPinDest.dest}`} className={classes.pinOption}>
                        <Grid item xs={12}>
                          <input
                            required
                            className={classes.pinSelectInput}
                            type="radio"
                            name="otpPinDest"
                            onChange={() => setOtpPinDest(otpPinDest)}
                          />
                          <span className={classes.etiqueta}>{otpPinDest.dest}</span>
                        </Grid>
                      </div>
                    ))}
                  </Grid>
                 
                </Grid>
              </Grid>
            </Grid>
          </>
        )}

        {/* formulario introducir pin */}
        {loginRes.loginPhase.status === "OTP_PIN" && (
          <>
            <Grid container direction="column">
              <Grid item xs={9}>
                <InfoMessage textKey="login.pinInput" className={classes.alert} />
              </Grid>
            </Grid>
            <Grid item xs={9}>
              <Grid container direction="row" alignItems="baseline">
                <Grid item xs={1} container direction="row" justify="flex-end">
                  <label htmlFor="password" className={clsx(classes.etiqueta, "etiquetaPassword")}>
                    {translate("ra.auth.otp_pin")}
                  </label>
                </Grid>
                <Grid item xs={6}>
                  <input
                    className={clsx(classes.entradas, "entradas")}
                    id="otpPin"
                    name="otpPin"
                    type="password"
                    value={otpPin}
                    onChange={(e) => setOtpPin(e.target.value)}
                  />
                </Grid>
              </Grid>
            </Grid>
          </>
        )}

        {/* botones comunes */}
        <Grid container direction="row" item xs={12} className={clsx(classes.buttonArea, "alrevesEnR")}>
          <Grid item xs={6} container>
            <Grid item xs={12} container direction="row" justify={loginRes.loginPhase.status === "USER_PASSWORD" && "flex-end"} className="alrevesEnR">
              <Button
                className={clsx(classes.btnSubmit, "btnSubmit","capitalizedInT")}
                type="submit"
                disabled={
                  (loginRes.loginPhase.status === "OTP_PIN_DESTS" && !otpPinDest) || loading || !password || !login
                }
                startIcon={<ButtonIcon loading={loading} className={classes.spin} />}
              >
                {/* de momento mismo texto - ya sale spinner: loading ? translate("ra.auth.entrar_en") : translate("ra.auth.entrar_en") */}
                {translate(loginRes.loginPhase.status !== "OTP_PIN_DESTS" ? "ra.auth.entrar" : "ra.auth.continuar")}
              </Button>
            </Grid>
            <Grid item xs={12} container direction="row" justify={loginRes.loginPhase.status === "USER_PASSWORD" && "flex-end"} className="alrevesEnR">
              {loading && (
                <Typography variant="body2" color="textSecondary" className={classes.submitMessage}>
                  {translate("login.wait_for_enter")}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Grid>
      </form>
      <Notification />
    </>
  );
};

const PersonalizedLoginForm = (props) => (
  <GoogleReCaptchaProvider reCaptchaKey="6LcnpUQdAAAAAFTRiEmv2B7IQKwGKjxJ16pCd4pq">
    <Form {...props} />
  </GoogleReCaptchaProvider>
);

PersonalizedLoginForm.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(PersonalizedLoginForm);
